import { useEffect, useRef } from 'react';

import useControlPanelOptions from 'utils/hooks/meteorology/useControlPanelOptions';
import useClassicComparatorVariable from 'utils/hooks/meteorology/classicComparator/useClassicComparatorVariable';
import useClassicComparatorBaseModel from 'utils/hooks/meteorology/classicComparator/useClassicComparatorBaseModel';
import useClassicComparatorConfrontingModel from 'utils/hooks/meteorology/classicComparator/useClassicComparatorConfrontingModel';
import {
  resolveDefaultVariableId,
  resolveDefaultModelValue,
} from 'helper/Meteorology/Fields/ClassicComparator/openDefaults';

/**
 * Aplica os defaults de abertura do Comparador Clássico (#1822, item 5.3):
 * variável = Precipitação e ambos os modelos = Indicação Met.
 *
 * É uma cascata em duas fases num único efeito one-shot: primeiro seleciona a
 * variável (isso reseta o slice), no render seguinte — com a variável já
 * definida — resolve e seleciona os modelos. As guardas por campo (pristine =
 * undefined) evitam sobrescrever escolha do usuário; o ref `done` garante que
 * roda uma única vez por montagem, então trocar a variável depois não
 * re-dispara os defaults.
 */
export default function useApplyClassicComparatorOpenDefaults() {
  const { variables, getModelsForVariable, isFetching } = useControlPanelOptions('comparador');
  const { variable, setVariable } = useClassicComparatorVariable();
  const { baseModel, setBaseModel } = useClassicComparatorBaseModel();
  const { confrontingModel, setConfrontingModel } = useClassicComparatorConfrontingModel();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    if (isFetching) return;
    if (variables.length === 0) return;

    // Fase 1: variável. Enquanto pristine, seleciona Precipitação e espera o
    // próximo render (a variável precisa propagar antes de resolver modelos).
    if (variable === undefined) {
      const variableId = resolveDefaultVariableId(variables);
      if (variableId) setVariable(variableId);
      return;
    }

    // Fase 2: modelos. Com a variável definida, resolve Indicação Met entre os
    // modelos daquela variável e preenche cada modelo ainda pristine.
    const models = getModelsForVariable(variable);
    if (models.length === 0) return;

    const modelValue = resolveDefaultModelValue(models);
    if (modelValue) {
      if (baseModel === undefined) setBaseModel(modelValue);
      if (confrontingModel === undefined) setConfrontingModel(modelValue);
    }
    done.current = true;
  }, [
    isFetching,
    variables,
    variable,
    baseModel,
    confrontingModel,
    getModelsForVariable,
    setVariable,
    setBaseModel,
    setConfrontingModel,
  ]);
}
